// TODO: Cleanup these, validate where called and then change to reject with Error objects
/* eslint-disable @typescript-eslint/prefer-promise-reject-errors */

import callGraphQLApi, {
  createGraphQLFailureAction,
  createGraphQLSuccessAction,
} from './callGraphQLApi';
import { Thunk } from '@worker/services/interfaces';
import { RedactedMediaAsset } from '@common-modules/mediaDetails/models';

// ACTIONS
export const FETCH_INTEGRATION_INFO =
  'externalIntegration/FETCH_INTEGRATION_INFO';
export const FETCH_INTEGRATION_INFO_SUCCESS =
  createGraphQLSuccessAction<FetchIntegrationInfoResponse>(
    'externalIntegration/FETCH_INTEGRATION_INFO_SUCCESS'
  );
export const FETCH_INTEGRATION_INFO_FAILURE = createGraphQLFailureAction(
  'externalIntegration/FETCH_INTEGRATION_INFO_FAILURE'
);

export interface ExternalIntegrationConfig {
  url: string;
  body: {
    fileId: string;
    fileName: string;
    folderId: string;
    folderName: string;
    auditLogUrl: string;
    reviewStatus: string;
    redactedMediaUrl: string;
    redactedTranscriptUrl: string;
  };
  label: string;
  method: string;
  headers: {
    [key: string]: string;
  };
}

interface SendFileReqPayload {
  tdoId: string;
  tdoName: string;
  folderId: string;
  folderName: string;
  reviewStatus: string;
  transcriptionURL: string;
  mediaFile: Pick<RedactedMediaAsset, 'name' | 'signedUri' | 'contentType'>;
  auditLog?: Pick<RedactedMediaAsset, 'name' | 'signedUri' | 'contentType'>;
  integrationConfig: ExternalIntegrationConfig;
}

function addFilenameSuffix<T extends Pick<RedactedMediaAsset, 'name'>>(
  file: T,
  suffix: string
): T {
  if (file.name) {
    const fn = file.name.split('.');
    fn[0] += suffix;
    return {
      ...file,
      name: fn.join('.'),
    };
  }
  return file;
}

const replaceIntegrationConfig = (
  value: string,
  sendFileReqPayload: SendFileReqPayload
) => {
  const {
    tdoId,
    tdoName,
    folderId,
    folderName,
    reviewStatus,
    transcriptionURL,
    mediaFile,
    auditLog,
  } = sendFileReqPayload;

  return value
    .replace(/{{{tdoId}}}/g, tdoId || '')
    .replace(/{{{tdoName}}}/g, tdoName || '')
    .replace(/{{{folderId}}}/g, folderId || '')
    .replace(/{{{folderName}}}/g, folderName || '')
    .replace(/{{{reviewStatus}}}/g, reviewStatus || '')
    .replace(/{{{redactedTranscriptUrl}}}/g, transcriptionURL || '')
    .replace(/{{{redactedMediaUrl}}}/g, mediaFile?.signedUri || '')
    .replace(/{{{auditLogUrl}}}/g, auditLog?.signedUri || '');
};

export const sendFile = (sendFileReqPayload: SendFileReqPayload) => {
  const {
    mediaFile: mediaFileRaw,
    auditLog: auditLogRaw,
    integrationConfig,
  } = sendFileReqPayload;

  const mediaFile = addFilenameSuffix(mediaFileRaw, '_redacted');
  const auditLog =
    auditLogRaw && addFilenameSuffix(auditLogRaw, '_redactionlog');

  return new Promise((resolve, reject) => {
    const xmlhttp = new XMLHttpRequest();

    const updatedSendFileReqPayload = {
      ...sendFileReqPayload,
      mediaFile,
      auditLog,
    };

    const url = replaceIntegrationConfig(
      integrationConfig.url,
      updatedSendFileReqPayload
    );
    const method = integrationConfig.method;
    const updatedHeaders = replaceIntegrationConfig(
      JSON.stringify(integrationConfig.headers),
      updatedSendFileReqPayload
    );
    // TODO: Parsing of JSON should be validated
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const header: { [key: string]: string } = JSON.parse(updatedHeaders);
    const updatedBody = replaceIntegrationConfig(
      JSON.stringify(integrationConfig.body),
      updatedSendFileReqPayload
    );

    xmlhttp.onerror = () => {
      console.log('xmlhttp.response', xmlhttp);
      reject();
    };

    xmlhttp.ontimeout = () => {
      reject();
    };

    xmlhttp.onload = () => {
      if (xmlhttp.status >= 200 && xmlhttp.status < 300) {
        resolve(null /* JSON.parse(xmlhttp.response)*/); // actual API doesn't return a json here
      } else {
        reject(xmlhttp.statusText);
      }
    };

    xmlhttp.open(method, url);

    Object.entries(header).forEach(([key, value]) => {
      xmlhttp.setRequestHeader(key, value);
    });

    xmlhttp.send(updatedBody);
  });
};

export interface FetchIntegrationInfoResponse {
  me: {
    organization: {
      id: string;
      integrationConfig: {
        config: ExternalIntegrationConfig;
      };
    };
  };
}

export const fetchIntegrationInfo: Thunk<void, FetchIntegrationInfoResponse> =
  () => async (dispatch, getState) => {
    const query = `
      query getExternalIntegrationInfo {
        me {
          organization {
            id
            integrationConfig(id: "redact-export") {
              config
            }
          }
        }
      }
    `;

    return await callGraphQLApi<FetchIntegrationInfoResponse>({
      actionTypes: [
        FETCH_INTEGRATION_INFO,
        FETCH_INTEGRATION_INFO_SUCCESS,
        FETCH_INTEGRATION_INFO_FAILURE,
      ],
      query,
      variables: {},
      dispatch,
      getState,
      bailout: undefined,
    });
  };
